import type { Observable } from 'rxjs';
import type { Object3D } from 'three';
import {
	EntityInteractionEventType,
	type SelectionUpdatedEventPayload,
} from '@puppycad/cad-core';
import type {
	CreateGeometryReferenceWorkflowDependenciesOptions,
	GeometryReferenceWorkflowDependencies,
} from '../../../types/editor/GeometryReferenceWorkflow';

export type {
	CreateGeometryReferenceWorkflowDependenciesOptions,
	GeometryReferenceWorkflowDependencies,
} from '../../../types/editor/GeometryReferenceWorkflow';

/**
 * 创建 Convert Entities 工作流依赖集合。
 *
 * 说明：
 * 1. 本函数只适配 UI 上下文、选择输入与 Viewer 任务门面；
 * 2. 文档级 Repository、Service 和 Coordinator 均由 Viewer 内部 Controller 持有；
 * 3. 每次命令独占的 Preview 会话仍由 workflow 负责及时释放。
 *
 * @param options 当前 Editor 的 Viewer 与草图上下文。
 * @returns 供 Convert Entities 工作流消费的任务集合。
 */
export function createGeometryReferenceWorkflowDependencies(
	options: CreateGeometryReferenceWorkflowDependenciesOptions,
): GeometryReferenceWorkflowDependencies {
	return {
		getViewer: () => options.getViewer(),
		getCurrentSketchGroupId: () => {
			const runtimeSketchId = options.currentSketchGroupId.value;
			if (!runtimeSketchId) {
				return null;
			}
			return options.getViewer()
				?.resolveSketchIdentity(runtimeSketchId)?.persistentId ?? null;
		},
		getSelectedObjects: () => options.getViewer()?.getSelectedObjects?.() ?? [],
		clearSelection: () => {
			options.getViewer()?.clearSelectionProgrammatically?.();
		},
		observeSelectionUpdated: () => {
			const observable = options.getViewer()
				?.observeEntityInteraction(EntityInteractionEventType.SelectionUpdated) ?? null;
			return observable as Observable<SelectionUpdatedEventPayload> | null;
		},
		resolveSelectionCandidate: (targetObject: Object3D, targetSketchId, resolverOptions) => (
			options.getViewer()?.resolveSketchEntityReferenceSelectionCandidate({
				targetObject,
				targetSketchId,
				options: resolverOptions,
			}) ?? Promise.resolve(null)
		),
		resolveAllInnerLoopCandidates: faceSources => (
			options.getViewer()?.resolveSketchEntityReferenceAllInnerLoopCandidates({
				faceSources,
			}) ?? Promise.resolve([])
		),
		getSelectionMode: () => options.getViewer()?.getSelectionMode?.() ?? null,
		setSelectionMode: (mode, selectionOptions) => {
			options.getViewer()?.setSelectionMode?.(mode, selectionOptions);
		},
		createPreviewSession: () => (
			options.getViewer()?.createSketchEntityReferencePreviewSession() ?? null
		),
		commitReferences: input => (
			options.getViewer()?.commitSketchEntityReferences(input) ?? null
		),
		removeRelation: input => (
			options.getViewer()?.removeSketchEntityReferenceRelation(input) ?? null
		),
		ensureReferenceRuntime: () => (
			options.getViewer()?.ensureSketchEntityReferenceRuntime() ?? false
		),
	};
}
